/** 달력 한 칸 안의 기간 이름 자리 — 몇째 주(row) · 무슨 요일(col) · 몇째 줄(lane) */
export interface LabelSlot {
  row: number;
  col: number;
  lane: number;
  label: string;
}

const LABEL_INSET = 4;
const LABEL_GAP = 4;
/** 이름 폭 어림 — 11px 굵은 글자: 한글 11px, 그 밖 6.5px, 좌우 여백 8px */
const labelWidth = (text: string) =>
  [...text].reduce((w, ch) => w + (ch.charCodeAt(0) > 0x1100 ? 11 : 6.5), 0) + 8;

/**
 * 기간 이름을 옆으로 미는 거리(px)를 slots 순서대로 돌려준다(음수면 왼쪽).
 *
 * 줄 간격(7px)이 이름 높이(13px)보다 좁아 이웃 줄(줄 차 1) 이름은 위아래로 겹칠 수 있다 — 같은 주에서
 * 이웃 줄 이름끼리 가로로 부딪히면 뒤에 놓는 이름을 오른쪽으로 민다. 주 끝을 넘으면 왼쪽으로 민다.
 * 놓는 차례는 위 주부터, 아래 줄부터, 왼쪽부터. 칸 폭은 렌더된 달력에서 잰 값을 받는다.
 */
export function placeSpanLabels(slots: readonly LabelSlot[], cellWidth: number): number[] {
  type Placed = { row: number; lane: number; x: number; w: number };
  const placed: Placed[] = [];
  const offsets = new Array<number>(slots.length).fill(0);
  const order = slots
    .map((slot, index) => ({ slot, index }))
    .sort(
      (a, b) => a.slot.row - b.slot.row || a.slot.lane - b.slot.lane || a.slot.col - b.slot.col
    );

  for (const { slot, index } of order) {
    const w = labelWidth(slot.label);
    const home = slot.col * cellWidth + LABEL_INSET;
    const clash = (x: number) =>
      placed.find(
        (p) =>
          p.row === slot.row && Math.abs(p.lane - slot.lane) <= 1 && x < p.x + p.w && p.x < x + w
      );
    let x = home;
    for (let tries = 0; tries < 4; tries++) {
      const hit = clash(x);
      if (!hit) break;
      const right = hit.x + hit.w + LABEL_GAP;
      x = right + w <= 7 * cellWidth ? right : Math.max(0, hit.x - LABEL_GAP - w);
    }
    offsets[index] = x - home;
    placed.push({ row: slot.row, lane: slot.lane, x, w });
  }
  return offsets;
}
